/**
 * Usage rules — guide §6.5, §7; ADR-001 §8.3, §10.1; the prototype's p5x part (uFc, uHist, uAlerts, uChip,
 * feedState, prodTip, upRead). Pure functions: the API (on apply / typed-in figures, and on read) and the worker
 * (nightly) call the same code, so two screens — or a screen and an alert — can never disagree.
 */
import { normaliseHeader, UPLOAD_MAX_BYTES, USAGE_SOURCE_LABEL } from '@amplispot/contracts';
// ============================================================================ configuration (ADR §8.3)
/** Mirrors DEFAULT_THRESHOLDS.forecast; `min_r2` is the fit below which the line is not drawn (`meaningful: false`). */
export const FORECAST = { damping: 0.6, window_days: 14, horizon_days: 30, util_cap: 160, min_r2: 0.3 };
/** util above this is treated as bad data rather than a real figure (the contract's ceiling). */
export const UTIL_MAX = 400;
/** ADR §10.1: pg_trgm-style similarity floor for a loose client match. */
export const MATCH_FLOOR = 0.6;
export const UPLOAD_MAX_ROWS = 5000;
/**
 * The widest report the reader accepts (sf-95d78953), counted after trailing all-empty columns are dropped. Every
 * staged row keeps a `raw` object with one key per column, so the width multiplies the 5 000-row cap: 1 000 columns
 * of a 67 KB CSV took the worker past its 256 MB heap. Real usage and history exports are a few dozen columns wide.
 */
export const UPLOAD_MAX_COLUMNS = 200;
/**
 * The longest header row the reader accepts, all header names together, trimmed (sf-256d836a). Every staged row's `raw`
 * repeats every header name as a key, so the staging JSON grows with rows x total header length: one 60 000-character
 * header (a 136 KB CSV) made 288 MB of JSON, and 200 headers of 200 characters 197 MB, both past the worker's 256 MB heap.
 * 4 000 characters (200 columns of 20) measured 27 MB of JSON and inserts fine; real headers are a few hundred.
 * Counted as the staging JSON writes the names, escapes included (sf-631ddb97): a control character is six characters
 * there, so names of them are refused outright, and a quote, backslash, tab or line break counts as two.
 */
export const UPLOAD_MAX_HEADER_CHARS = 4000;
/**
 * The most the cells of one report may weigh once read, all data cells together (sf-46194873), counted as the staging
 * JSON writes them (escapes included, quotes not) and doubled for a cell holding any character above U+00FF, which
 * V8 keeps at two bytes a character, in the reader's reply as in the worker. One byte of an upload stages to at most
 * two characters (a backslash, quote, tab or line break; control characters are dropped) of at most two bytes, so no
 * CSV within UPLOAD_MAX_BYTES can stage past 4x it, and an .xlsx without sharing is held to its 8 MB zip bounds. Only
 * an .xlsx shared string, stored once and referenced by every cell, went past it: a 0.1 MB workbook read as a 100 MB table.
 */
export const UPLOAD_MAX_STAGED_CELL_BYTES = 4 * UPLOAD_MAX_BYTES;
/**
 * A client cell longer than this is "Client not found" without being matched (sf-3cd55876): client names are at most
 * 120 characters, and matching costs rows x clients x name length, so a 2 000-character cell blocked the worker.
 */
export const MATCH_NAME_MAX = 200;
/** Prototype PRIO weights. */
export const PRIORITY_WEIGHT = { high: 3, med: 2, low: 1 };
// ============================================================================ periods and dates
/** `2026-10-16` → `2026-10`. */
export const periodOf = (isoDate) => isoDate.slice(0, 7);
/** `addMonths('2026-01', -2)` → `2025-11`. */
export function addMonths(period, n) {
    const [y, m] = period.split('-').map(Number);
    const idx = y * 12 + (m - 1) + n;
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}
/** `addDays('2026-10-16', -13)`. */
export function addDays(isoDate, n) {
    const d = new Date(`${isoDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}
export const daysBetween = (fromIso, toIso) => Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);
/** Today in Asia/Kolkata as `YYYY-MM-DD` (ADR §9: all business dates are IST). */
export const todayIst = (now = Date.now()) => new Date(now + 330 * 60_000).toISOString().slice(0, 10);
/** The IST calendar date of an instant. */
export const istDateOf = (t) => new Date(new Date(t).getTime() + 330 * 60_000).toISOString().slice(0, 10);
// ============================================================================ numbers
const round = (n) => Math.round(n);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
/** % of plan used. `null` without a figure or a plan. Capped at UTIL_MAX. */
export const utilOf = (used, plan) => used == null || plan == null || plan <= 0 ? null : Math.min(UTIL_MAX, round((used / plan) * 100));
/** % change of `now` against `ref`; `null` when there is nothing to compare against. */
export const pctChange = (now, ref) => now == null || ref == null || ref <= 0 ? null : round(((now - ref) / ref) * 100);
/**
 * ADR §10.1 number cleaning: strip `,` `%` `₹` `hrs` and whitespace; anything left that is not a plain
 * non-negative number is rejected (`null`). Numbers already typed as numbers (Excel cells) pass through.
 */
export function cleanNumber(v) {
    if (v == null)
        return null;
    if (typeof v === 'number')
        return Number.isFinite(v) && v >= 0 ? v : null;
    const s = String(v).toLowerCase().replace(/hrs?\b|₹|,|%|\s+/g, '');
    if (!/^\d+(\.\d+)?$|^\.\d+$/.test(s))
        return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}
// ============================================================================ header and client matching (guide §7)
/** A header that can be the client column: `client | customer | account | brand | company | name`. */
export const CLIENT_HEADER = /client|customer|account|brand|company|name/;
/** A code column ("Customer ID", "Account no", "Client code"): it carries the name word but not the name. */
const ID_HEADER = /(^| )(id|no|number|code|ref)$/;
/**
 * The client column, best first: a name column for the client (`Client name`, `Company Name`), then any other
 * client-word column that is not a code (`Client`, `Brand`), then a bare name column (`Name`), and only then a
 * code column (`Customer ID`) — so `Customer ID, Customer Name` reads names, not codes. Ties go to the leftmost.
 * No candidate → column 0. `h` is NORMALISED headers (normaliseHeader); the upload pop-up uses the same picker so its
 * "Client in the report" column shows the value the server matched on.
 */
export function clientColumn(h) {
    const rank = (x) => {
        if (!CLIENT_HEADER.test(x))
            return 9;
        if (ID_HEADER.test(x))
            return 3;
        const entity = /client|customer|account|brand|company/.test(x);
        const named = /(^| )name( |$)/.test(x);
        return entity && named ? 0 : entity ? 1 : 2;
    };
    let best = -1;
    let bestRank = 9;
    h.forEach((x, i) => {
        const r = rank(x);
        if (r < bestRank)
            [best, bestRank] = [i, r];
    });
    return best < 0 ? 0 : best;
}
/** `a` contains `b` as whole words: "calls made total" ⊇ "calls made", but "videos watched" ⊉ "id". */
const hasWords = (a, b) => ` ${a} `.includes(` ${b} `);
/**
 * Find each field's column (the prototype's upRead, hardened). For a field, its label and aliases are tried in
 * three passes, each over EVERY name before the next pass starts:
 *   1. an exact normalised match;
 *   2. whole-word containment either way ("Calls made (total)" ⊇ "calls made");
 *   3. plain substring either way, only when the shorter side has 3+ characters (the matchClient guard).
 * So a short header like "ID" can never be read as "Videos watched" (it is inside "v-id-eos"), and an alias's
 * exact match beats the label's loose one. A field that matches nothing is left out rather than guessed.
 * The client column and a column already claimed by an earlier field are never reused; the main field goes first.
 */
export function resolveColumns(headers, fields) {
    const h = headers.map((x) => normaliseHeader(String(x ?? '')));
    const clientCol = clientColumn(h);
    const taken = new Set([clientCol]);
    const cols = {};
    const passes = [
        (x, n) => x === n,
        (x, n) => hasWords(x, n) || hasWords(n, x),
        (x, n) => Math.min(x.length, n.length) >= 3 && (x.includes(n) || n.includes(x)),
    ];
    const ordered = [...fields].sort((a, b) => Number(b.is_main) - Number(a.is_main));
    for (const f of ordered) {
        const names = [f.label, ...f.aliases].map(normaliseHeader).filter(Boolean);
        let at = -1;
        for (const pass of passes) {
            for (const n of names) {
                at = h.findIndex((x, i) => !taken.has(i) && x !== '' && pass(x, n));
                if (at >= 0)
                    break;
            }
            if (at >= 0)
                break;
        }
        if (at >= 0) {
            cols[f.field_key] = at;
            taken.add(at);
        }
    }
    return { clientCol, cols };
}
/** pg_trgm's trigram set: lower-case words, each padded with two spaces before and one after. */
function trigrams(s) {
    const out = new Set();
    for (const w of s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)) {
        const p = `  ${w} `;
        for (let i = 0; i + 3 <= p.length; i++)
            out.add(p.slice(i, i + 3));
    }
    return out;
}
/** pg_trgm `similarity()`: shared trigrams over all distinct trigrams (0..1). */
export function similarity(a, b) {
    return setSimilarity(trigrams(a), trigrams(b));
}
/** similarity() over two trigram sets already built; walks the smaller set, so a long name costs one pass, not one per client. */
function setSimilarity(x, y) {
    if (!x.size || !y.size)
        return 0;
    const [small, big] = x.size <= y.size ? [x, y] : [y, x];
    let shared = 0;
    for (const t of small)
        if (big.has(t))
            shared++;
    return shared / (x.size + y.size - shared);
}
/**
 * ADR §10.1: exact on the normalised name, then mutual substring, then trigram similarity with a 0.6 floor.
 * More than one candidate at a step → `ambiguous` (the reviewer picks). Never silently guesses.
 */
export function matchClient(name, clients) {
    return clientMatcher(clients)(name);
}
/**
 * matchClient for many names against one client list (an uploaded report's rows), with identical results
 * (sf-3cd55876). The clients are indexed once — normalised name → clients for the exact step, and each client's
 * trigram set — and each name's own trigram set is built once, not once per client. The answer depends only on the
 * normalised name, so a repeated name is answered from a cache that lives as long as the returned function.
 * Each row is then one pass over the clients with no per-client re-normalising or set building, so staging a report
 * is about linear in its rows (a 10 MB file used to hold the worker's event loop for over a minute).
 */
export function clientMatcher(clients) {
    const index = clients.map((c) => ({ c, n: normaliseHeader(c.name), tri: trigrams(c.name) }));
    const byName = new Map();
    for (const x of index) {
        const same = byName.get(x.n);
        if (same)
            same.push(x.c);
        else
            byName.set(x.n, [x.c]);
    }
    const seen = new Map();
    const decide = (n) => {
        const exact = byName.get(n) ?? [];
        if (exact.length === 1)
            return { kind: 'exact', client: exact[0], candidates: [], score: 1 };
        if (exact.length > 1)
            return { kind: 'ambiguous', client: null, candidates: [...exact], score: 1 };
        const tri = trigrams(n);
        const score = (x) => Math.round(setSimilarity(tri, x.tri) * 1000) / 1000;
        // Mutual substring needs 3+ characters on the shorter side, so "a" or "co" never matches everything. Only the
        // shorter of the two can sit inside the longer (equal lengths would be the exact step, already ruled out).
        const sub = index.filter((x) => Math.min(x.n.length, n.length) >= 3 && (x.n.length >= n.length ? x.n.includes(n) : n.includes(x.n)));
        if (sub.length === 1)
            return { kind: 'fuzzy', client: sub[0].c, candidates: [], score: score(sub[0]) };
        if (sub.length > 1)
            return { kind: 'ambiguous', client: null, candidates: sub.map((x) => x.c), score: Math.max(...sub.map(score)) };
        const near = index.map((x) => ({ c: x.c, s: score(x) })).filter((x) => x.s >= MATCH_FLOOR).sort((a, b) => b.s - a.s);
        if (near.length === 1)
            return { kind: 'fuzzy', client: near[0].c, candidates: [], score: near[0].s };
        if (near.length > 1)
            return { kind: 'ambiguous', client: null, candidates: near.map((x) => x.c), score: near[0].s };
        return { kind: 'none', client: null, candidates: [], score: null };
    };
    return (name) => {
        const n = normaliseHeader(name);
        if (!n)
            return { kind: 'none', client: null, candidates: [], score: null };
        let m = seen.get(n);
        if (!m)
            seen.set(n, (m = decide(n)));
        // A fresh object per call: one caller's edit to a result never leaks into another row's.
        return { ...m, candidates: [...m.candidates] };
    };
}
// ============================================================================ thresholds (Settings)
/** Prototype rule: the watch line is at least max(60, low_line + 10), and never above 100. */
export const watchLineFor = (requested, lowLine) => Math.min(100, Math.max(requested, 60, lowLine + 10));
// ============================================================================ chips, freshness, tips
/** Prototype uChip. */
export const toneOf = (util, low, watch) => util == null ? 'info' : util < low ? 'cold' : util < watch ? 'warm' : 'hot';
/**
 * Prototype feedState. `daysSinceLast` is days since the last figure of ANY period (null = never).
 * `limit` is the freshness limit for the source in force.
 */
export function feedState(daysSinceLast, hasUtil, limit) {
    if (daysSinceLast == null)
        return { kind: 'waiting', tone: 'info', label: 'Waiting for first data', days_since_last: null };
    const d = daysSinceLast;
    if (d > limit * 2 || (!hasUtil && d > limit))
        return { kind: 'no_data', tone: 'cold', label: `No data for ${d} days`, days_since_last: d };
    if (d > limit)
        return { kind: 'late', tone: 'warm', label: `Late by ${d - limit} days`, days_since_last: d };
    return { kind: 'fresh', tone: 'hot', label: d === 0 ? 'Fresh today' : `Updated ${d}d ago`, days_since_last: d };
}
/** The freshness limit: the product's own setting, unless this client's source differs from the product's. */
export const freshnessLimit = (productSource, clientSource, productLimit) => clientSource && clientSource !== productSource ? USAGE_SOURCE_LABEL[clientSource].limit_days : productLimit;
/** Prototype TIPS, verbatim. */
export const TIPS = {
    microsite: { low: 'Share a top-agents leaderboard and ask the client to push microsite links on their next agent call.', fall: 'Pageviews are falling. Refresh the banner with the current offer and share a short DIY video on sharing the link.', high: 'Pageviews are strong. Ask for a video testimonial and show their leadership the leads captured.' },
    loyalty: { low: 'Launch a short contest with a visible leaderboard and post weekly winners in the WhatsApp group.', fall: 'Participation is dropping. Refresh the contest theme and add a small mid-month reward.', high: 'Strong participation. Share the results with leadership and propose a second contest.' },
    engage: { low: 'Review the journeys with the client and add one or two new triggers, such as a festival or a renewal reminder.', fall: 'Volumes are falling. Check for paused journeys and refresh the creative.', high: 'Healthy sending. Ask for a testimonial and look at adding a new journey.' },
    learn: { low: 'Run a short training for the field team and send a DIY video on opening the first reel.', fall: 'Learner activity is dropping. Send a new reel pack and a nudge from their manager.', high: 'Learners are active. Share completion and quiz scores with the sponsor and ask for a testimonial.' },
    pulse: { low: 'Ask outlet or branch teams to share the review link at billing or after service.', fall: 'Reviews are slowing. Refresh the QR placement and remind the teams.', high: 'Reviews are coming in well. Share the rating trend with leadership.' },
    collect: { low: 'Review the email calendar with the client and add one more send per month with a fresh template.', fall: 'Opens are slipping. Refresh the look and subject lines.', high: 'Strong results. Suggest a new segment or campaign.' },
    contacts: { low: 'Upload the latest contact lists and run an enrichment pass.', fall: 'Few new contacts. Ask the client for fresh lists.', high: 'Database is growing. Suggest a campaign to the verified contacts.' },
};
const TIP_DEFAULT = { low: 'Run a refresher training and share a DIY video.', fall: 'Usage is falling. Find out why on the next call.', high: 'Strong use. Ask for a testimonial.' };
/** Prototype prodTip: low, then falling, then strong (85%+); otherwise no tip (`''`). */
export function tipFor(productKey, util, trend, low, fallPct) {
    if (util == null)
        return '';
    const t = TIPS[productKey] ?? TIP_DEFAULT;
    if (util < low)
        return t.low;
    if (trend != null && trend <= -fallPct)
        return t.fall;
    if (util >= 85)
        return t.high;
    return '';
}
// ============================================================================ estimated history (guide §6.5)
/**
 * Six past months, oldest first, estimated from the client's own usual level (`base`) and current trend,
 * as the prototype's uHist without its cosmetic noise: the month before this one equals `base`, and each
 * earlier month drifts by −trend × 0.16 per month. Real figures always replace estimates (ADR §8.3).
 */
export function estimatedMonth(base, trend, monthsBack) {
    if (base == null)
        return null;
    const k = monthsBack - 1; // 1 month back → k = 0 → exactly base
    const tr = (trend ?? 0) / 100;
    return Math.max(0, round(base * (1 - tr * 0.16 * k)));
}
/**
 * The 14-day line before real daily snapshots exist: a straight line from the client's usual level
 * (base as % of plan) 13 days ago to today's utilisation. Shows the same direction as `trend`, ends at
 * today's figure, and is always flagged as an estimate.
 */
export function estimatedDayUtil(baseUtil, utilNow, index, last = FORECAST.window_days - 1) {
    if (utilNow == null)
        return null;
    const from = baseUtil ?? utilNow;
    return Math.round((from + ((utilNow - from) * index) / last) * 10) / 10;
}
/**
 * Least squares over the daily utilisation series (x = index 0..n-1; days without a figure are left out),
 * slope damped by `cfg.damping`, projected `cfg.horizon_days`, clamped 0..`cfg.util_cap` (defaults 0.6, 30,
 * 160: FORECAST). `null` with fewer than 2 points or no current figure. A series of equal values is fitted
 * exactly by a flat line, so r² = 1 (never NaN). `util_in_30d` keeps its contract name whatever the horizon.
 */
export function forecast(series, utilNow, lowLine, plan, cfg = FORECAST) {
    if (utilNow == null || plan == null)
        return null;
    const pts = series.map((y, x) => ({ x, y })).filter((p) => p.y != null);
    const n = pts.length;
    if (n < 2)
        return null;
    const mx = pts.reduce((a, p) => a + p.x, 0) / n;
    const my = pts.reduce((a, p) => a + p.y, 0) / n;
    let sxy = 0;
    let sxx = 0;
    let syy = 0;
    for (const p of pts) {
        sxy += (p.x - mx) * (p.y - my);
        sxx += (p.x - mx) ** 2;
        syy += (p.y - my) ** 2;
    }
    const slope = sxx ? sxy / sxx : 0;
    const intercept = my - slope * mx;
    let ssRes = 0;
    for (const p of pts)
        ssRes += (p.y - (intercept + slope * p.x)) ** 2;
    const r2 = syy === 0 ? 1 : clamp(1 - ssRes / syy, 0, 1);
    const damped = slope * cfg.damping;
    const fu = clamp(round(utilNow + damped * cfg.horizon_days), 0, cfg.util_cap);
    const days = slope < 0 && utilNow > lowLine ? round((utilNow - lowLine) / -damped) : null;
    return {
        points: n,
        slope,
        intercept,
        damped_slope: damped,
        util_in_30d: fu,
        days_to_low_line: days,
        forecast_abs: Math.max(0, round((plan * fu) / 100)),
        r2: Math.round(r2 * 100000) / 100000,
    };
}
const nf = (n) => (n == null ? '-' : Math.round(n).toLocaleString('en-IN'));
/**
 * The alert rules, each its own card. As in the prototype the three level rules are a chain — already below
 * the low line, else forecast under the low line, else forecast under the watch line while falling — so one
 * product never shows two cards saying the same thing. `four_months_decline` needs four REAL months (an
 * alert is never raised from estimated history). `no_data_reported` fires only when there is no figure for
 * this period AND the feed is not fresh, so the first days of a month do not raise one for every client.
 */
export function evaluateAlerts(a) {
    const out = [];
    const base = { low_line: a.low_line, watch_line: a.watch_line, plan: a.plan };
    if (a.util == null) {
        if (a.feed.kind === 'fresh')
            return out;
        const d = a.feed.days_since_last;
        out.push({
            rule: 'no_data_reported',
            severity: 'info',
            is_forecast: false,
            why: d == null
                ? 'Nothing has been uploaded for this product, so there is nothing to compare.'
                : `No figure for this month yet, and the last one was ${d} days ago, so there is nothing to compare.`,
            numbers: { ...base, days_since_last: d },
        });
        return out;
    }
    // A weak fit is no forecast at all (ADR §8.3, t-8390b908): the two forecast rules stay silent and every alert's
    // util_in_30d / days_to_low_line are null, so no "At this pace it falls to N%" comes from a noisy line.
    const f = a.forecast && a.forecast.r2 >= (a.min_r2 ?? FORECAST.min_r2) ? a.forecast : null;
    const numbers = { ...base, util: a.util, used: a.used, base: a.base, trend: a.trend, util_in_30d: f?.util_in_30d ?? null, days_to_low_line: f?.days_to_low_line ?? null };
    const down = a.vs_last_month;
    if (down != null && down <= -a.fall_pct) {
        out.push({
            rule: 'down_15_vs_last_month',
            severity: down <= -25 || a.util < a.low_line ? 'at_risk' : 'watch',
            is_forecast: false,
            why: `Down ${-down}% on last month (${nf(a.last_month)} to ${nf(a.used)} ${a.unit})`,
            numbers: { ...numbers, fall_pct: a.fall_pct, vs_last_month: down, last_month: a.last_month },
        });
    }
    if (a.util < a.low_line) {
        out.push({ rule: 'util_below_low_line', severity: 'at_risk', is_forecast: false, why: `Already below the ${a.low_line}% low-use line (${a.util}% of plan)`, numbers });
    }
    else if (f && f.util_in_30d < a.low_line) {
        out.push({
            rule: 'forecast_below_low_line',
            severity: 'at_risk',
            is_forecast: true,
            why: `At this pace it falls to ${f.util_in_30d}% of plan${f.days_to_low_line ? ` in about ${f.days_to_low_line} days` : ''}, under the ${a.low_line}% low-use line`,
            numbers: { ...numbers, forecast_abs: f.forecast_abs, r2: f.r2 },
        });
    }
    else if (f && f.util_in_30d < a.watch_line && a.trend != null && a.trend < 0) {
        out.push({
            rule: 'forecast_below_watch_falling',
            severity: 'watch',
            is_forecast: true,
            why: `At this pace it ends up at ${f.util_in_30d}% of plan, under the ${a.watch_line}% watch line`,
            numbers: { ...numbers, forecast_abs: f.forecast_abs, r2: f.r2 },
        });
    }
    const m = a.months;
    const [m4, m3, m2] = [m[m.length - 3], m[m.length - 2], m[m.length - 1]];
    if (a.used != null && m4 && m3 && m2 &&
        !m4.is_estimated && !m3.is_estimated && !m2.is_estimated &&
        m4.value != null && m3.value != null && m2.value != null &&
        m4.value > m3.value && m3.value > m2.value && m2.value > a.used) {
        out.push({
            rule: 'four_months_decline',
            severity: 'watch',
            is_forecast: false,
            why: 'Down four months in a row',
            numbers: { ...numbers, [m4.period]: m4.value, [m3.period]: m3.value, [m2.period]: m2.value },
        });
    }
    return out;
}
/**
 * Worst-first score (ADR §8.3): priority_weight × 3 + (at_risk ? 4 : 2) + mrr / 2 + (forecast ? 1 : 0).
 * A "no data" card scores priority_weight × 2 (prototype). `mrr` is passed only for managers: a member's
 * order uses priority and severity alone — the documented consequence of hiding revenue.
 */
export function alertScore(priority, severity, isForecast, mrrLakh) {
    const w = PRIORITY_WEIGHT[priority] ?? 1;
    if (severity === 'info')
        return w * 2;
    const s = w * 3 + (severity === 'at_risk' ? 4 : 2) + (mrrLakh ?? 0) / 2 + (isForecast ? 1 : 0);
    return Math.round(s * 100) / 100;
}
//# sourceMappingURL=usage.js.map